import { useState, useEffect } from 'react';
import { PlatformLayout } from '../components/layout/PlatformLayout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { FileText, Clock, Search, Loader2, ChevronRight } from 'lucide-react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(d) {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch { return d; }
}

function DocTab({ doc, isActive, onClick }) {
  return (
    <button
      data-testid={`doc-tab-${doc.key}`}
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-all whitespace-nowrap ${
        isActive
          ? 'bg-primary text-primary-foreground font-medium shadow-sm'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
      }`}
    >
      <FileText className="w-3.5 h-3.5 shrink-0" />
      <span>{doc.title}</span>
      {doc.lines > 0 && (
        <Badge variant="outline" className="text-[10px] ml-1 px-1.5 py-0">{doc.lines}L</Badge>
      )}
    </button>
  );
}

function MarkdownRenderer({ content }) {
  return (
    <div className="prose prose-sm dark:prose-invert max-w-none
      prose-headings:scroll-mt-4
      prose-h1:text-xl prose-h1:font-bold prose-h1:border-b prose-h1:pb-2 prose-h1:mb-4
      prose-h2:text-lg prose-h2:font-semibold prose-h2:mt-6 prose-h2:mb-3
      prose-h3:text-base prose-h3:font-medium prose-h3:mt-4
      prose-table:text-xs
      prose-th:px-3 prose-th:py-1.5 prose-th:bg-muted prose-th:font-medium
      prose-td:px-3 prose-td:py-1.5 prose-td:border-t
      prose-code:text-xs prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded
      prose-pre:bg-zinc-900 prose-pre:text-zinc-100 prose-pre:text-xs prose-pre:rounded-lg
      prose-a:text-primary prose-a:no-underline hover:prose-a:underline
      prose-blockquote:border-l-primary prose-blockquote:bg-muted/50 prose-blockquote:py-1 prose-blockquote:rounded-r
    ">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
    </div>
  );
}

function SearchBar({ value, onChange }) {
  return (
    <div className="relative" data-testid="docs-search">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
      <input
        type="text"
        placeholder="Rechercher dans le document..."
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full pl-9 pr-4 py-2 text-sm border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
      />
    </div>
  );
}

function TableOfContents({ content, onNavigate }) {
  const headings = [];
  const lines = content.split('\n');
  for (const line of lines) {
    const match = line.match(/^(#{1,3})\s+(.+)/);
    if (match) {
      headings.push({ level: match[1].length, text: match[2].replace(/[*_`]/g, '') });
    }
  }
  if (headings.length === 0) return null;

  return (
    <div className="space-y-0.5" data-testid="docs-toc">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Sommaire</p>
      {headings.slice(0, 30).map((h, i) => (
        <button
          key={i}
          onClick={() => onNavigate(h.text)}
          className={`block w-full text-left text-xs py-1 hover:text-primary transition-colors truncate ${
            h.level === 1 ? 'font-medium text-foreground' :
            h.level === 2 ? 'pl-3 text-muted-foreground' :
            'pl-6 text-muted-foreground/70'
          }`}
        >
          <ChevronRight className="w-3 h-3 inline mr-1" />
          {h.text}
        </button>
      ))}
    </div>
  );
}

export default function PlatformDocsPage() {
  const { token } = useAuth();
  const [docs, setDocs] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [content, setContent] = useState('');
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingContent, setLoadingContent] = useState(false);
  const [search, setSearch] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/platform/docs`, { headers })
      .then(r => {
        const list = r.data.docs.filter(d => d.exists);
        setDocs(list);
        if (list.length > 0) {
          setActiveKey(list[0].key);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!activeKey) return;
    setLoadingContent(true);
    setSearch('');
    axios.get(`${API}/platform/docs/${activeKey}`, { headers })
      .then(r => {
        setContent(r.data.content);
        setMeta(r.data);
      })
      .catch(() => setContent('Erreur de chargement du document.'))
      .finally(() => setLoadingContent(false));
  }, [activeKey]);

  const handleTocNavigate = (text) => {
    const el = document.querySelector(`[id="${text.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const filteredContent = search
    ? content.split('\n').filter(line =>
        line.toLowerCase().includes(search.toLowerCase())
      ).join('\n')
    : content;

  if (loading) {
    return (
      <PlatformLayout title="Documentation">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Chargement...</span>
        </div>
      </PlatformLayout>
    );
  }

  return (
    <PlatformLayout title="Documentation">
      <div className="space-y-4" data-testid="platform-docs-page">
        {/* Tabs */}
        <div className="flex items-center gap-1 overflow-x-auto pb-1 scrollbar-thin" data-testid="docs-tabs">
          {docs.map(doc => (
            <DocTab
              key={doc.key}
              doc={doc}
              isActive={activeKey === doc.key}
              onClick={() => setActiveKey(doc.key)}
            />
          ))}
        </div>

        {/* Meta bar */}
        {meta && (
          <div className="flex items-center gap-4 text-xs text-muted-foreground" data-testid="docs-meta">
            <span className="flex items-center gap-1">
              <FileText className="w-3 h-3" />
              {meta.filename}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Modifié le {formatDate(meta.last_modified)}
            </span>
            <Badge variant="outline" className="text-[10px]">{meta.lines} lignes</Badge>
          </div>
        )}

        {/* Content area with sidebar TOC */}
        <div className="flex gap-4">
          {/* Table of contents sidebar */}
          {content && !search && (
            <Card className="hidden lg:block w-64 shrink-0 self-start sticky top-4">
              <CardContent className="p-3 max-h-[calc(100vh-220px)] overflow-y-auto scrollbar-thin">
                <TableOfContents content={content} onNavigate={handleTocNavigate} />
              </CardContent>
            </Card>
          )}

          {/* Main content */}
          <div className="flex-1 min-w-0">
            <SearchBar value={search} onChange={setSearch} />

            <Card className="mt-3" data-testid="docs-content">
              <CardContent className="p-6">
                {loadingContent ? (
                  <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Chargement du document...</span>
                  </div>
                ) : search && filteredContent.trim() === '' ? (
                  <p className="text-muted-foreground text-sm py-8 text-center">
                    Aucun résultat pour "{search}"
                  </p>
                ) : (
                  <MarkdownRenderer content={filteredContent} />
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </PlatformLayout>
  );
}
